import Loader from 'components/Loader.tsx';
import MatterIndicators, { BOTTOM_LINE_IN_NEW_DESIGN } from 'components/matters/MatterIndicators';
import CoiCheckEmailSendButton from 'components/modals/coi/CoiCheckEmailSendModal';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { SearchTermContext, SearchTermHighlight } from 'components/SearchTermHighlight';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Tooltip } from 'components/ui/tooltip';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat.ts';
import isEqual from 'lodash/isEqual';
import { getLinks } from 'pages/matters/util.tsx';
import { DidYouMean } from 'pages/search/ResultCard';
import LabelledData from 'pages/timecards/LabelledData.tsx';
import React, { ReactNode, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    CoiCheckDetailsJson,
    CoiCheckRelation,
    CoiCheckScopeType,
    CoiCheckSummary,
    MatterDto,
    PartyDto,
    VisibilityScope,
} from 'types/coi-check';
import { NamedEntityMinDto } from 'types/other';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { CoiCheckAccordion } from './accordion/CoiCheckAccordion';
import {
    ContactOpeningCell,
    EmployeesAndBeneficialOwnersCell,
    IdentifiersCell,
    OpponentOpeningCell,
    UboForCompaniesCell,
} from './details/ContactTableCells.tsx';
import { MatterDescriptionCell, MatterOpeningCell, MatterRelatedContactsCell } from './details/MatterTableCells.tsx';
import { displayLocalNameIfExists, ENTITY_LINK_CLASS } from './util/CoiCheckUtil.ts';

interface Props {
    summary: CoiCheckSummary;
    /** Runs a new check on the backend's suggested spelling; without it the suggestion is not offered. */
    onDidYouMean?: (term: string) => void;
}

const parseExpensePathname = (pathname?: string): string => {
    if (!pathname) {
        return '/';
    }

    const pathElements = pathname.split('/');
    let location = '';

    if (pathElements.find((el) => el === 'matter')) {
        location = '?tab=expenses';
    } else if (pathElements.find((el) => el === 'effort')) {
        location = '#expenses-expenses';
    }

    return `${pathname}${location}`;
};

const ConnectedContacts: React.FC<{ domain: string; contacts: NamedEntityMinDto[] }> = React.memo(
    ({ domain, contacts }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        // The new design's chips stand apart with no comma, as `getLinks` draws them.
        const comma = useIsNewDesign() ? '' : ',\u00A0';

        if (!contacts || contacts.length === 0) {
            return null;
        }

        const firstItems = contacts.slice(0, 2);
        const remainingItems = contacts.slice(2);
        const firstLinks = getLinks(domain, firstItems);
        const remainingLinks = remainingItems.length > 0 ? getLinks(domain, remainingItems, { separator: null }) : [];

        return (
            <div>
                {firstLinks}
                {isExpanded ? (
                    remainingLinks.length > 0 ? (
                        [comma, ...remainingLinks]
                    ) : null
                ) : remainingLinks.length > 0 ? (
                    <Tooltip
                        key="connected-contacts-tooltip"
                        content={
                            <div>
                                {remainingLinks.map((link, idx) => (
                                    // oxlint-disable-next-line react-doctor/no-array-index-as-key -- link is an opaque ReactNode without accessible id
                                    <div key={idx}>{link}</div>
                                ))}
                            </div>
                        }
                    >
                        <button
                            type="button"
                            className="text-link text-body1 ml-1 cursor-pointer font-semibold"
                            onClick={() => setIsExpanded(!isExpanded)}
                        >
                            {`${comma}+${remainingItems.length}`}
                        </button>
                    </Tooltip>
                ) : null}
            </div>
        );
    },
    (prevProps, currentProps) =>
        prevProps.domain === currentProps.domain && isEqual(prevProps.contacts, currentProps.contacts)
);

/**
 * A COI result row that belongs to a case: the case indicators lead the row's own text. Rows the
 * viewer cannot open keep their muted styling and add the no-access circle; the padlock appears on
 * both, because confidentiality says nothing about access.
 */
const CoiCaseCell = ({ details, children }: { details: CoiCheckDetailsJson; children: ReactNode }) => {
    const hasAccess = details.visibility === VisibilityScope.FULL;
    return (
        <span className="new:flex-wrap flex items-center gap-4">
            <MatterIndicators
                isConfidential={details.isConfidential}
                hasAccess={hasAccess}
                className={BOTTOM_LINE_IN_NEW_DESIGN}
            />
            {hasAccess ? children : <span className="text-fg-secondary text-xs font-medium">{children}</span>}
        </span>
    );
};

const CoiCheckDetails: React.FC<Props> = ({ summary, onDidYouMean }) => {
    const effectiveFormat = useEffectiveDateFormat();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['EFFORT_ACTIVITY_TYPE', 'MATTER_STATE_TYPE'],
    });

    const contactColumns = useMemo<DataTableColumnDef<CoiCheckRelation>[]>(
        () => [
            {
                header: t('coi.names'),
                accessorFn: (row) => row,
                enableSorting: false,
                cell: ({ getValue }) => <ContactOpeningCell detailsJson={getValue<CoiCheckRelation>().detailsJson} />,
            },
            {
                header: t('coi.identifiers'),
                accessorFn: (row) => row,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation>();
                    return (
                        <IdentifiersCell
                            detailsJson={value.detailsJson}
                            entityName={value.entityName}
                            scopeType={value.coiCheckScopeTypeCode as CoiCheckScopeType}
                        />
                    );
                },
            },
            {
                header: t('coi.email'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (value.email ?? '') : null;
                },
            },
            {
                header: t('coi.coi-notes'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                meta: {
                    width: '40%',
                },
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (
                        <SearchTermHighlight text={value.notes} />
                    ) : null;
                },
            },
        ],
        []
    );

    const clientCompanyColumns = useMemo<DataTableColumnDef<CoiCheckRelation>[]>(
        () => [
            ...contactColumns,
            {
                header: t('coi.employees-and-beneficial-owners'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => (
                    <EmployeesAndBeneficialOwnersCell detailsJson={getValue<CoiCheckRelation['detailsJson']>()} />
                ),
            },
        ],
        [contactColumns]
    );

    const personColumns = useMemo<DataTableColumnDef<CoiCheckRelation>[]>(
        () => [
            ...contactColumns,
            {
                header: t('coi.ubo-for-companies'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => (
                    <UboForCompaniesCell detailsJson={getValue<CoiCheckRelation['detailsJson']>()} />
                ),
            },
        ],
        [contactColumns]
    );

    const previousCoiCheckColumns = useMemo<DataTableColumnDef<CoiCheckRelation>[]>(
        () => [
            {
                header: t('coi.date'),
                accessorFn: (row) => row.detailsJson?.date,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckDetailsJson['date']>();
                    return value ? format(new Date(value), `${effectiveFormat} HH:mm`) : '';
                },
            },
            {
                header: t('coi.keyword'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value ? (
                        <div>
                            <LinkWithOrigin className={ENTITY_LINK_CLASS} to={`/coi-check/${value.id}`}>
                                <SearchTermHighlight text={value.searchTerm} />
                            </LinkWithOrigin>
                        </div>
                    ) : (
                        ''
                    );
                },
            },
            {
                header: t('coi.author'),
                accessorFn: (row) => row.detailsJson?.employee,
                enableSorting: false,
                meta: {
                    width: '40%',
                },
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckDetailsJson['employee']>();
                    return value ? (
                        <div>
                            <RecordLink
                                withOrigin
                                className={ENTITY_LINK_CLASS}
                                to={`/employee/${value.id}`}
                                label={value.name ?? ''}
                            >
                                <SearchTermHighlight text={value.name} />
                            </RecordLink>
                        </div>
                    ) : (
                        ''
                    );
                },
            },
        ],
        [effectiveFormat]
    );

    const timecardColumns = useMemo<DataTableColumnDef<CoiCheckRelation>[]>(
        () => [
            {
                header: t('coi.date'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    const date = value.date ? format(new Date(value.date), effectiveFormat) : '';
                    return <CoiCaseCell details={value}>{date}</CoiCaseCell>;
                },
            },
            {
                header: t('coi.author'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL && value.employee ? (
                        <div>
                            <RecordLink
                                withOrigin
                                className={ENTITY_LINK_CLASS}
                                to={`/employee/${value.employee.id}`}
                                label={value.employee.name ?? ''}
                            >
                                <SearchTermHighlight text={value.employee.name} />
                            </RecordLink>
                        </div>
                    ) : (
                        <span className="text-caption text-fg-secondary font-medium">
                            <SearchTermHighlight text={value.employee?.name} />
                        </span>
                    );
                },
            },
            {
                header: t('coi.timecard-description'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                meta: {
                    width: '40%',
                },
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (
                        <div>
                            <LinkWithOrigin className={ENTITY_LINK_CLASS} to={`/timecards/${value.id}`}>
                                <SearchTermHighlight text={value.description} />
                            </LinkWithOrigin>
                        </div>
                    ) : null;
                },
            },
        ],
        [effectiveFormat]
    );

    const expenseColumns = useMemo<DataTableColumnDef<CoiCheckRelation>[]>(
        () => [
            {
                header: t('coi.date'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    const date = value.date ? format(new Date(value.date), effectiveFormat) : '';
                    return <CoiCaseCell details={value}>{date}</CoiCaseCell>;
                },
            },
            {
                header: t('coi.author'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL && value.employee ? (
                        <div>
                            <RecordLink
                                withOrigin
                                className={ENTITY_LINK_CLASS}
                                to={`/employee/${value.employee.id}`}
                                label={value.employee.name ?? ''}
                            >
                                <SearchTermHighlight text={value.employee.name} />
                            </RecordLink>
                        </div>
                    ) : (
                        <span className="text-caption text-fg-secondary font-medium">
                            <SearchTermHighlight text={value.employee?.name} />
                        </span>
                    );
                },
            },
            {
                header: t('coi.expense-description'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                meta: {
                    width: '40%',
                },
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (
                        <LinkWithOrigin className={ENTITY_LINK_CLASS} to={parseExpensePathname(value.pathname)}>
                            <SearchTermHighlight text={value.description} />
                        </LinkWithOrigin>
                    ) : null;
                },
            },
        ],
        [effectiveFormat]
    );

    const invoiceColumns = useMemo<DataTableColumnDef<CoiCheckRelation>[]>(
        () => [
            {
                header: t('coi.invoice-date'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    const date = value.date ? format(new Date(value.date), effectiveFormat) : '';
                    return <CoiCaseCell details={value}>{date}</CoiCaseCell>;
                },
            },
            {
                header: t('coi.invoice-number'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (
                        <div>
                            <RecordLink
                                withOrigin
                                className={ENTITY_LINK_CLASS}
                                to={`/invoice/${value.id}`}
                                label={value.number ?? ''}
                            />
                        </div>
                    ) : (
                        <span className="text-caption text-fg-secondary font-medium">{value.number ?? ''}</span>
                    );
                },
            },
            {
                header: t('coi.invoice-client-payer-name'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    const clientParties = (value.parties || []).filter((item: PartyDto) => item.role === 'CLIENT');
                    const payerParties = (value.parties || []).filter((item: PartyDto) => item.role === 'PAYER');

                    return value.visibility === VisibilityScope.FULL ? (
                        <div className="flex flex-col">
                            <div>{clientParties.length ? getLinks('contact', clientParties) : ''}</div>
                            <div>{payerParties.length ? getLinks('contact', payerParties) : ''}</div>
                        </div>
                    ) : null;
                },
            },
            {
                header: t('coi.matters-connected'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                meta: {
                    width: '40%',
                },
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (
                        <SearchTermHighlight text={value.description} />
                    ) : null;
                },
            },
        ],
        [effectiveFormat]
    );

    const effortColumns = useMemo<DataTableColumnDef<CoiCheckRelation>[]>(
        () => [
            {
                header: t('coi.date'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    const date = value.date ? format(new Date(value.date), effectiveFormat) : '';
                    return <CoiCaseCell details={value}>{date}</CoiCaseCell>;
                },
            },
            {
                header: t('coi.related-colleagues'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (
                        <div className="flex flex-col">
                            <LabelledData label="coi.effort-employee" className="text-body1">
                                {value?.employee ? (
                                    // The effort's employee: the chip goes to their record, classic keeps its `/group/` link.
                                    <RecordLink
                                        withOrigin
                                        className={ENTITY_LINK_CLASS}
                                        key={value.employee.id}
                                        to={`/group/${value.employee.id}`}
                                        entityType="EMPLOYEE"
                                        id={value.employee.id}
                                        label={value.employee.name ?? ''}
                                    >
                                        <SearchTermHighlight text={value.employee.name} />
                                    </RecordLink>
                                ) : null}
                            </LabelledData>
                            <LabelledData label="coi.effort-additional-employees" className="text-body1">
                                {value?.employees?.length ? getLinks('employee', value?.employees) : ''}
                            </LabelledData>
                        </div>
                    ) : (
                        <span className="text-caption text-fg-secondary font-medium">
                            <SearchTermHighlight text={value.employee?.name} />
                        </span>
                    );
                },
            },
            {
                header: t('coi.connected-contacts'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (
                        <div className="flex flex-col">
                            <LabelledData label="coi.persons" className="text-body1">
                                {value.partyPersons?.length ? (
                                    <ConnectedContacts domain="contact" contacts={value.partyPersons} />
                                ) : (
                                    ''
                                )}
                            </LabelledData>
                            <LabelledData label="coi.entities" className="text-body1">
                                {value.partyCompanies?.length ? (
                                    <ConnectedContacts domain="contact" contacts={value.partyCompanies} />
                                ) : (
                                    ''
                                )}
                            </LabelledData>
                        </div>
                    ) : null;
                },
            },
            {
                header: t('coi.effort-description'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                meta: {
                    width: '40%',
                },
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (
                        <div>
                            <LinkWithOrigin className={ENTITY_LINK_CLASS} to={`/effort/${value.id}`}>
                                <SearchTermHighlight text={value.description} />
                            </LinkWithOrigin>
                        </div>
                    ) : null;
                },
            },
        ],
        [effectiveFormat]
    );

    const employeeColumns = useMemo<DataTableColumnDef<CoiCheckRelation>[]>(
        () => [
            {
                header: t('coi.names'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (
                        <div className="flex flex-col">
                            <div className="flex items-center font-medium">
                                <PartyName
                                    party={{
                                        ...value,
                                        name:
                                            (value.name ?? '') +
                                            (displayLocalNameIfExists(value.localFirstName, value.localLastName) ?? ''),
                                    }}
                                    isLink
                                    useLinkWithOrigin
                                    variant="readonly-other"
                                />
                            </div>
                            <LabelledData label="coi.previous">
                                <SearchTermHighlight text={value.previousNames} />
                            </LabelledData>
                        </div>
                    ) : (
                        <CoiCaseCell details={value}>
                            <SearchTermHighlight text={value.name} />
                            <SearchTermHighlight
                                text={displayLocalNameIfExists(value.localFirstName, value.localLastName)}
                            />
                        </CoiCaseCell>
                    );
                },
            },
            {
                header: t('coi.identifiers'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (
                        <div className="flex flex-col">
                            <LabelledData label="coi.personal-id" className="text-body1">
                                {value.personalIdCode ?? ''}
                            </LabelledData>
                        </div>
                    ) : null;
                },
            },
            {
                header: t('coi.email'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (value.email ?? '') : null;
                },
            },
            {
                header: t('coi.notes'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                meta: {
                    width: '40%',
                },
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (
                        <SearchTermHighlight text={value.notes} />
                    ) : null;
                },
            },
        ],
        []
    );

    const matterColumns = useMemo<DataTableColumnDef<CoiCheckRelation>[]>(
        () => [
            {
                header: t('coi.matter-employees-opening'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => (
                    <MatterOpeningCell
                        detailsJson={getValue<CoiCheckRelation['detailsJson']>()}
                        classifiers={classifiers}
                    />
                ),
            },
            {
                header: t('coi.matter-related-contacts'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => (
                    <MatterRelatedContactsCell detailsJson={getValue<CoiCheckRelation['detailsJson']>()} />
                ),
            },
            {
                header: t('coi.description'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                meta: {
                    width: '40%',
                },
                cell: ({ getValue }) => (
                    <MatterDescriptionCell detailsJson={getValue<CoiCheckRelation['detailsJson']>()} />
                ),
            },
        ],
        [classifiers]
    );

    const opponentColumns = useMemo<DataTableColumnDef<CoiCheckRelation>[]>(
        () => [
            {
                header: t('coi.names'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => (
                    <OpponentOpeningCell detailsJson={getValue<CoiCheckRelation['detailsJson']>()} />
                ),
            },
            {
                header: t('coi.identifiers'),
                accessorFn: (row) => row,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation>();
                    return (
                        <IdentifiersCell
                            detailsJson={value.detailsJson}
                            entityName={value.entityName}
                            scopeType={value.coiCheckScopeTypeCode as CoiCheckScopeType}
                        />
                    );
                },
            },
            {
                header: t('coi.matters'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL
                        ? (value.matters || []).map((item: MatterDto) => (
                              <div key={item.id}>
                                  <RecordLink
                                      withOrigin
                                      className={ENTITY_LINK_CLASS}
                                      to={`/matter/${item.id}`}
                                      label={[item.number, item.name].filter(Boolean).join(' ')}
                                  >
                                      {item.number} <SearchTermHighlight text={item.name} />
                                  </RecordLink>
                              </div>
                          ))
                        : null;
                },
            },
            {
                header: t('coi.coi-notes'),
                accessorFn: (row) => row.detailsJson,
                enableSorting: false,
                meta: {
                    width: '40%',
                },
                cell: ({ getValue }) => {
                    const value = getValue<CoiCheckRelation['detailsJson']>();
                    return value.visibility === VisibilityScope.FULL ? (
                        <SearchTermHighlight text={value.notes} />
                    ) : null;
                },
            },
        ],
        []
    );

    const accordionsData = [
        {
            scopeType: CoiCheckScopeType.CLIENT,
            titleId: 'coi.clients',
            tooltipId: 'coi.tooltip.client-search',
            columns: clientCompanyColumns,
        },
        {
            scopeType: CoiCheckScopeType.COMPANY,
            titleId: 'coi.companies',
            tooltipId: 'coi.tooltip.party-company-search',
            columns: clientCompanyColumns,
        },
        {
            scopeType: CoiCheckScopeType.OPPONENT,
            titleId: 'coi.opponents',
            tooltipId: 'coi.tooltip.opponent-search',
            columns: opponentColumns,
        },
        {
            scopeType: CoiCheckScopeType.MATTER,
            titleId: 'coi.matters',
            tooltipId: 'coi.tooltip.matter-search',
            columns: matterColumns,
        },
        {
            scopeType: CoiCheckScopeType.PERSON,
            titleId: 'coi.persons',
            tooltipId: 'coi.tooltip.party-person-search',
            columns: personColumns,
        },
        {
            scopeType: CoiCheckScopeType.TIMECARD,
            titleId: 'coi.timecards',
            tooltipId: 'coi.tooltip.timecard-search',
            columns: timecardColumns,
        },
        {
            scopeType: CoiCheckScopeType.EXPENSE,
            titleId: 'coi.expenses',
            tooltipId: 'coi.tooltip.expense-search',
            columns: expenseColumns,
        },
        {
            scopeType: CoiCheckScopeType.INVOICE,
            titleId: 'coi.invoices',
            tooltipId: 'coi.tooltip.invoice-search',
            columns: invoiceColumns,
        },
        {
            scopeType: CoiCheckScopeType.EFFORT,
            titleId: 'coi.efforts',
            tooltipId: 'coi.tooltip.effort-search',
            columns: effortColumns,
        },
        {
            scopeType: CoiCheckScopeType.EMPLOYEE,
            titleId: 'employees.colleagues',
            tooltipId: 'coi.tooltip.employee-search',
            columns: employeeColumns,
        },
        {
            scopeType: CoiCheckScopeType.PREVIOUS_COI,
            titleId: 'coi.similar-searches',
            tooltipId: 'coi.tooltip.previous-coi-search',
            columns: previousCoiCheckColumns,
        },
    ];

    const { didYouMean } = summary;
    const checkedAt = `${t('coi.on')} ${
        summary?.checkedAt ? format(new Date(summary.checkedAt), `${effectiveFormat} HH:mm`) : ''
    }`;

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <>
            <div className="flex items-center justify-between">
                <div className="my-2 flex flex-1">
                    {`${t('coi.searched-for')} "`}
                    <span className="font-bold">{summary.searchTerm}</span>
                    {`" ${checkedAt}`}
                </div>
                <CoiCheckEmailSendButton
                    className="w-[90px]"
                    variant="contained"
                    color="secondary"
                    size="extraSmall"
                    coiCheckEmailData={[
                        {
                            id: summary.id,
                            searchTerm: summary.searchTerm,
                            checkedAt: summary.checkedAt,
                        },
                    ]}
                >
                    <FormattedMessage id="coi.send-email.title" />
                </CoiCheckEmailSendButton>
            </div>
            {didYouMean && onDidYouMean && (
                <p className="mb-2">
                    <DidYouMean term={didYouMean} onSelect={onDidYouMean} />
                </p>
            )}
            <SearchTermContext value={summary.searchTerm}>
                <div>
                    {accordionsData.map((accordion) => (
                        <CoiCheckAccordion
                            key={`${summary.id}-${accordion.scopeType}`}
                            countsByScope={summary.countsByScopeType}
                            scopeType={accordion.scopeType}
                            titleId={accordion.titleId}
                            tooltipId={accordion.tooltipId}
                            coiCheckId={summary.id}
                            columns={accordion.columns}
                        />
                    ))}
                </div>
            </SearchTermContext>
        </>
    );
};

export default CoiCheckDetails;
